import React, { useMemo } from 'react';
import { Paper, makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { TileLayer, Marker, MapContainer } from 'react-leaflet';
import L, { LatLngExpression } from 'leaflet';
import { GPSPosition } from '@moderepo/mode-apis';
import { ModeTheme } from '@moderepo/mode-app-components';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            height   : '100%',
            padding  : 5,
            minHeight: 300,
        },

        map: {
            width    : '100%',
            height   : '100%',
            minHeight: 400,
        },

        marker: {

            '& .marker-pin': {
                // Draw shadow
                width       : 2,
                height      : 2,
                borderRadius: '50%',
                position    : 'absolute',
                left        : '50%',
                top         : '50%',
                margin      : '-1px 0 0 -1px',
                boxShadow   : '0 0 6px 10px rgba(0, 0, 0, 0.4)',

                // Drawer marker
                '&:before': {
                    content     : '""',
                    width       : 40,
                    height      : 40,
                    borderRadius: '50% 50% 50% 0',
                    border      : '2px solid #ffffff',
                    background  : '#2479d4',
                    position    : 'absolute',
                    transform   : 'rotate(-45deg)',
                    left        : '50%',
                    top         : '50%',
                    margin      : '-45px 0 0 -20px',
                },

                // Draw white circle
                '&:after': {
                    content     : '""',
                    width       : 26,
                    height      : 26,
                    margin      : '-37px 0 0 -12px',
                    background  : theme.palette.background.paper,
                    position    : 'absolute',
                    borderRadius: '50%',
                },
            },

            // Icon
            '& .icon': {
                position : 'absolute',
                width    : 28,
                fontSize : 18,
                right    : 0,
                top      : -8,
                left     : -13,
                textAlign: 'center',
            },

            '&.normal': {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.success.main,
                    },
                },
            },

            '&.warning': {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.warning.main,
                    },
                },
            },

            '&.critical': {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.error.main,
                    },
                },
            },

        },
    };
});


interface MapMetricValueDisplayProps {
    readonly className?: string;
    readonly data?: GPSPosition;
    readonly center?: [number, number];
}


/**
 * This component is used for displaying metric value using map
 * @param props
 */
export const MapMetricValueDisplay = (props: MapMetricValueDisplayProps) => {
    const classes = useStyle();
    const theme = useTheme() as ModeTheme;


    const markerIcon = props.data ? L.divIcon({
        className  : clsx(classes.marker),
        html       : '<div class="marker-pin"></div><i class="icon mode-icon robot-solid"/>',
        iconSize   : [2, 54],
        iconAnchor : [0, 30],
        popupAnchor: [1, -42],
    }) : undefined;


    // Use useMemo to create a mapCenter so that we can cache it
    const mapCenter = useMemo((): LatLngExpression => {
        return props.center ? props.center : [0, 0];
    }, [props.center]);


    return (
        <Paper className={clsx(classes.root, props.className)}>
            <MapContainer
                key={mapCenter.toString()}
                className={classes.map}
                center={mapCenter}
                zoom={18}
            >
                <TileLayer
                    attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
                    url={`${theme.mapTilesURL}?key=0FuuQf9TW9Dp1F7i0rsp`}
                />
                {props.data && (
                    <Marker
                        position={[props.data.latitude, props.data.longitude]}
                        icon={markerIcon}
                    />
                )}
            </MapContainer>
        </Paper>
    );
};
